import React from 'react';
import { Plus, Trash2, Image as ImageIcon, Video, Check, Type, HelpCircle, Lightbulb, MessageSquareText } from 'lucide-react';
import { ModuleStepInput, InfoStepContent, CheckpointStepContent, ConfirmationStepContent, QuizStepContent, QuizQuestion, AuthoredQuestionType } from '@/lib/models/onboarding';
import { MarkdownEditor, LinkAssistPopover } from '@/components/custom-ui';
import { onboardingApi } from '@/lib/api/onboardingApi';
import { sourcesApi } from '@/lib/api/sourcesApi';

// ModuleStepEditor — the per-step content editor shown when a step row is
// expanded in ManualModuleForm. One sub-editor per step type. Body fields that
// the course player renders as Markdown (info/confirmation body) use the shared
// MarkdownEditor — wired with the standardised Link affordances (image upload,
// selection "Fråga Link", toolbar "Fråga Link") exactly like the description.
// Plain-text fields (titles, questions, options, explanations) stay as inputs
// since the player renders them verbatim.

interface ModuleStepEditorProps {
    step: ModuleStepInput;
    onChange: (updated: ModuleStepInput) => void;
    moduleName?: string;
}

// A MarkdownEditor pre-wired with the standardised Link affordances for a step
// body: image upload + selection assist + a toolbar "Fråga Link" popover.
const BodyEditor: React.FC<{
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    minHeightClass?: string;
    moduleName?: string;
}> = ({ value, onChange, placeholder, minHeightClass, moduleName }) => (
    <MarkdownEditor
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        minHeightClass={minHeightClass}
        onImageUpload={async (file) => {
            const src = await sourcesApi.uploadSource(file);
            return sourcesApi.getPublicImageUrl(src.id);
        }}
        selectionAssistRun={(action, instruction, selectedText) => onboardingApi.aiAssistModule({
            action: action as any,
            content: selectedText,
            instruction,
            module_name: moduleName,
            scope: 'selection',
        })}
        toolbarExtra={
            <LinkAssistPopover
                subjectText={value}
                run={(action, instruction) => onboardingApi.aiAssistModule({
                    action: action as any,
                    content: value,
                    instruction,
                    module_name: moduleName,
                })}
                onApply={(revised) => onChange(revised)}
            />
        }
    />
);

const INPUT =
    'w-full px-3.5 py-2.5 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent transition-shadow';

const FieldLabel: React.FC<{ children: React.ReactNode; icon?: React.ComponentType<any> }> = ({ children, icon: Icon }) => (
    <label className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-gray-400" />} {children}
    </label>
);

const Field: React.FC<{ label: string; icon?: React.ComponentType<any>; hint?: string; children: React.ReactNode }> = ({ label, icon, hint, children }) => (
    <div>
        <FieldLabel icon={icon}>{label}</FieldLabel>
        {children}
        {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
);

// OptionRow — one answer option for checkpoint / quiz. The "correct" toggle is a
// green check pill (far clearer than a bare radio); the whole row tints green
// when it's the right answer.
const OptionRow: React.FC<{
    letter: string;
    text: string;
    isCorrect: boolean;
    canRemove: boolean;
    onText: (v: string) => void;
    onCorrect: () => void;
    onRemove: () => void;
}> = ({ letter, text, isCorrect, canRemove, onText, onCorrect, onRemove }) => (
    <div className={`flex items-center gap-2.5 p-2 pl-2.5 rounded-xl border transition-colors ${isCorrect ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'}`}>
        <button
            type="button"
            onClick={onCorrect}
            title="Markera som rätt svar"
            className={`flex items-center justify-center w-6 h-6 rounded-full border-2 flex-shrink-0 transition-colors ${
                isCorrect ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300 text-transparent hover:border-gray-400'
            }`}
        >
            <Check className="w-3.5 h-3.5" />
        </button>
        <span className={`text-xs font-bold w-4 flex-shrink-0 ${isCorrect ? 'text-green-700' : 'text-gray-400'}`}>{letter}</span>
        <input
            type="text"
            value={text}
            onChange={(e) => onText(e.target.value)}
            placeholder={`Alternativ ${letter}`}
            className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none placeholder:text-gray-400"
        />
        {canRemove && (
            <button type="button" onClick={onRemove} className="p-1 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
            </button>
        )}
    </div>
);

const ModuleStepEditor: React.FC<ModuleStepEditorProps> = ({ step, onChange, moduleName }) => {
    const updateContent = (content: any) => onChange({ ...step, content });
    const updateTitle = (title: string) => onChange({ ...step, step_title: title });

    return (
        <div className="space-y-4">
            <Field label="Stegtitel" icon={Type}>
                <input
                    type="text"
                    value={step.step_title}
                    onChange={(e) => updateTitle(e.target.value)}
                    placeholder="t.ex. Välkommen, Kontroll: Säkerhet, etc."
                    className={INPUT}
                />
            </Field>

            {step.step_type === 'info' && <InfoEditor content={step.content} onChange={updateContent} moduleName={moduleName} />}
            {step.step_type === 'checkpoint' && <CheckpointEditor content={step.content} onChange={updateContent} />}
            {step.step_type === 'confirmation' && <ConfirmationEditor content={step.content} onChange={updateContent} moduleName={moduleName} />}
            {step.step_type === 'quiz' && <QuizEditor content={step.content} onChange={updateContent} />}
        </div>
    );
};

// ─── INFO ────────────────────────────────────────────────────────────────────
const InfoEditor: React.FC<{ content: InfoStepContent; onChange: (c: InfoStepContent) => void; moduleName?: string }> = ({ content, onChange, moduleName }) => (
    <>
        <Field label="Rubrik" icon={Type}>
            <input type="text" value={content.heading || ''} onChange={(e) => onChange({ ...content, heading: e.target.value })} placeholder="En tydlig rubrik för steget" className={INPUT} />
        </Field>

        {/* Media — image + video, grouped + previewed */}
        <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3.5 space-y-3">
            <p className="text-xs font-semibold text-gray-600 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5 text-gray-400" /> Media <span className="font-normal text-gray-400">(valfritt, visas högst upp)</span></p>
            <div>
                <input type="url" value={content.image_url || ''} onChange={(e) => onChange({ ...content, image_url: e.target.value || undefined })} placeholder="Bild-URL — https://…" className={INPUT} />
                {content.image_url && (
                    <img src={content.image_url} alt="" className="mt-2 w-full max-h-44 object-cover rounded-lg border border-gray-200" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                )}
            </div>
            <div className="relative">
                <Video className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input type="url" value={content.video_url || ''} onChange={(e) => onChange({ ...content, video_url: e.target.value || undefined })} placeholder="Video-URL — YouTube, Vimeo eller .mp4" className={`${INPUT} pl-9`} />
            </div>
        </div>

        <Field label="Innehåll">
            <BodyEditor value={content.body || ''} onChange={(v) => onChange({ ...content, body: v })} placeholder="Skriv informationen här — rubriker, fetstil, listor…" minHeightClass="min-h-[160px]" moduleName={moduleName} />
        </Field>
    </>
);

// ─── ANSWER TYPE (shared: checkpoint + every quiz question) ──────────────────
//
// #293. Two shapes, one control. "Flerval" is the graded one that has always
// existed; "Fritext" collects prose the employer reads afterwards and grades
// nothing.
//
// Switching to Fritext does NOT delete the authored options — they stay in
// content, hidden, so flipping back does not silently destroy work somebody
// typed. The renderer and the validator both branch on question_type, so unused
// options are inert rather than confusing.
const AnswerTypePicker: React.FC<{
    value: AuthoredQuestionType | undefined;
    onChange: (t: AuthoredQuestionType) => void;
    compact?: boolean;
}> = ({ value, onChange, compact }) => {
    // Content authored before #293 has no question_type at all. It was always
    // multiple choice, so that is what "no value" means here — the same
    // assumption the renderers make.
    const current: AuthoredQuestionType = value === 'text_input' ? 'text_input' : 'multiple_choice';
    const options: { id: AuthoredQuestionType; label: string; hint: string }[] = [
        { id: 'multiple_choice', label: 'Flerval', hint: 'Deltagaren väljer ett alternativ. Rättas automatiskt.' },
        { id: 'text_input', label: 'Fritext', hint: 'Deltagaren skriver ett eget svar. Rättas inte — du läser svaret.' },
    ];

    return (
        <div className={compact ? '' : 'space-y-1.5'}>
            {!compact && (
                <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                    <MessageSquareText className="w-3.5 h-3.5 text-gray-400" /> Svarstyp
                </span>
            )}
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                {options.map((o) => (
                    <button
                        key={o.id}
                        type="button"
                        title={o.hint}
                        onClick={() => onChange(o.id)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            current === o.id
                                ? 'bg-white text-[#1A4D4F] shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        {o.label}
                    </button>
                ))}
            </div>
            <p className="text-xs text-gray-500">{options.find((o) => o.id === current)!.hint}</p>
        </div>
    );
};

// ─── CHECKPOINT (single question) ────────────────────────────────────────────
const CheckpointEditor: React.FC<{ content: CheckpointStepContent; onChange: (c: CheckpointStepContent) => void }> = ({ content, onChange }) => {
    const isOpenAnswer = content.question_type === 'text_input';
    // options is optional now that a question may be free text, so every access
    // goes through this rather than assuming an array is there.
    const opts = content.options ?? [];
    const updateOption = (index: number, text: string) => {
        const newOptions = [...opts];
        newOptions[index] = { ...newOptions[index], text };
        onChange({ ...content, options: newOptions });
    };
    const addOption = () => {
        const nextLetter = String.fromCharCode(65 + opts.length);
        onChange({ ...content, options: [...opts, { option_id: nextLetter, text: '' }] });
    };
    const removeOption = (index: number) => onChange({ ...content, options: opts.filter((_, i) => i !== index) });

    return (
        <>
            <Field label="Rubrik" icon={Type}>
                <input type="text" value={content.heading || ''} onChange={(e) => onChange({ ...content, heading: e.target.value })} placeholder="Valfri rubrik" className={INPUT} />
            </Field>
            <Field label="Frågetext" icon={HelpCircle}>
                <textarea value={content.question_text || ''} onChange={(e) => onChange({ ...content, question_text: e.target.value })} rows={2} placeholder="Vad ska deltagaren svara på?" className={`${INPUT} resize-none`} />
            </Field>
            <AnswerTypePicker
                value={content.question_type}
                onChange={(question_type) => onChange({ ...content, question_type })}
            />
            {!isOpenAnswer && (
            <Field label="Svarsalternativ" hint="Markera det rätta svaret med den gröna bocken.">
                <div className="space-y-2">
                    {opts.map((opt, i) => (
                        <OptionRow
                            key={opt.option_id}
                            letter={opt.option_id}
                            text={opt.text}
                            isCorrect={content.correct_option_id === opt.option_id}
                            canRemove={opts.length > 2}
                            onText={(v) => updateOption(i, v)}
                            onCorrect={() => onChange({ ...content, correct_option_id: opt.option_id })}
                            onRemove={() => removeOption(i)}
                        />
                    ))}
                </div>
                <button type="button" onClick={addOption} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#1A4D4F] hover:text-[#1A4D4F]/80">
                    <Plus className="w-3.5 h-3.5" /> Lägg till alternativ
                </button>
            </Field>
            )}
            <ExplanationFields
                value={content}
                onChange={(patch) => onChange({ ...content, ...patch })}
                // With no right or wrong there is no outcome to write a per-outcome
                // override for — only the general text, shown to everyone after they
                // answer. Useful for revealing a model answer.
                generalOnly={isOpenAnswer}
                generalLabel={isOpenAnswer ? 'Text efter svar' : undefined}
            />
        </>
    );
};

// ─── EXPLANATION FIELDS (shared: checkpoint + every quiz question) ───────────
//
// #294. Two things at once: the general explanation, and optional per-outcome
// overrides ("förklaring vid RÄTT svar" / "vid FEL svar").
//
// The overrides sit behind a disclosure rather than always on screen. A quiz can
// hold many questions, and three textareas each would turn authoring a five-
// question quiz into fifteen boxes for a feature most courses will not use. The
// disclosure opens by itself when either override already has text, so an
// authored course never hides content from the person editing it.
const ExplanationFields: React.FC<{
    value: { explanation?: string; explanation_correct?: string; explanation_incorrect?: string };
    onChange: (patch: { explanation?: string; explanation_correct?: string; explanation_incorrect?: string }) => void;
    /** Compact = inside a quiz question card (no Field chrome, smaller inputs). */
    compact?: boolean;
    /**
     * #293 — free text has no right or wrong, so there is no outcome to write a
     * per-outcome override for. Hide the split entirely rather than offering
     * "Vid RÄTT svar" on a question nothing grades.
     */
    generalOnly?: boolean;
    /** Overrides the Field label when the text is not really an "explanation". */
    generalLabel?: string;
}> = ({ value, onChange, compact = false, generalOnly = false, generalLabel }) => {
    const authored = !!(value.explanation_correct || value.explanation_incorrect);
    const [open, setOpen] = React.useState(authored);
    // An authored course opened for editing must reveal what it already has.
    React.useEffect(() => { if (authored) setOpen(true); }, [authored]);

    const general = (
        <textarea
            value={value.explanation || ''}
            onChange={(e) => onChange({ ...value, explanation: e.target.value })}
            rows={2}
            placeholder={
                generalOnly
                    ? 'T.ex. "Ett bra svar nämner rutinen i kapitel 4." (valfritt)'
                    : open
                        ? 'Visas när inget svarsspecifikt alternativ är ifyllt'
                        : 'Varför är detta rätt svar?'
            }
            className={`${INPUT} resize-none`}
        />
    );

    const split = open && (
        <div className="mt-2 space-y-2">
            <textarea
                value={value.explanation_correct || ''}
                onChange={(e) => onChange({ ...value, explanation_correct: e.target.value })}
                rows={2}
                placeholder="Vid RÄTT svar (valfritt)"
                className={`${INPUT} resize-none`}
            />
            <textarea
                value={value.explanation_incorrect || ''}
                onChange={(e) => onChange({ ...value, explanation_incorrect: e.target.value })}
                rows={2}
                placeholder="Vid FEL svar (valfritt)"
                className={`${INPUT} resize-none`}
            />
        </div>
    );

    const toggle = (
        <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-[#1A4D4F] hover:text-[#1A4D4F]/80"
        >
            <Lightbulb className="w-3.5 h-3.5" />
            {open ? 'Använd samma förklaring för båda' : 'Olika förklaring vid rätt och fel svar'}
        </button>
    );

    if (compact) {
        return <div>{general}{!generalOnly && split}{!generalOnly && toggle}</div>;
    }
    return (
        <Field
            label={generalLabel || 'Förklaring'}
            icon={Lightbulb}
            hint={generalOnly
                ? 'Valfri. Visas efter att deltagaren skickat sitt svar — t.ex. ett exempelsvar.'
                : 'Visas efter att deltagaren svarat.'}
        >
            {general}
            {!generalOnly && split}
            {!generalOnly && toggle}
        </Field>
    );
};

// ─── CONFIRMATION ────────────────────────────────────────────────────────────
const ConfirmationEditor: React.FC<{ content: ConfirmationStepContent; onChange: (c: ConfirmationStepContent) => void; moduleName?: string }> = ({ content, onChange, moduleName }) => (
    <>
        <Field label="Rubrik" icon={Type}>
            <input type="text" value={content.heading || ''} onChange={(e) => onChange({ ...content, heading: e.target.value })} placeholder="t.ex. Bekräfta att du läst policyn" className={INPUT} />
        </Field>
        <Field label="Innehåll">
            <BodyEditor value={content.body || ''} onChange={(v) => onChange({ ...content, body: v })} placeholder="Vad ska deltagaren läsa och bekräfta?" minHeightClass="min-h-[140px]" moduleName={moduleName} />
        </Field>
        <Field label="Bekräftelsetext" icon={Check} hint="Texten bredvid kryssrutan deltagaren måste bocka i.">
            <input type="text" value={content.checkbox_label || ''} onChange={(e) => onChange({ ...content, checkbox_label: e.target.value })} placeholder="Jag har läst och förstått…" className={INPUT} />
        </Field>
    </>
);

// ─── QUIZ (multiple questions) ───────────────────────────────────────────────
const QuizEditor: React.FC<{ content: QuizStepContent; onChange: (c: QuizStepContent) => void }> = ({ content, onChange }) => {
    const isOpen = (q: QuizQuestion) => q.question_type === 'text_input';
    const addQuestion = () => {
        const newQ: QuizQuestion = {
            question_id: (content.questions?.length || 0) + 1,
            question_text: '',
            question_type: 'multiple_choice',
            options: [
                { option_id: 'A', text: '' },
                { option_id: 'B', text: '' },
            ],
            correct_option_id: 'A',
            explanation: '',
        };
        onChange({ ...content, questions: [...(content.questions || []), newQ] });
    };
    const updateQuestion = (index: number, updated: QuizQuestion) => {
        const newQuestions = [...content.questions];
        newQuestions[index] = updated;
        onChange({ ...content, questions: newQuestions });
    };
    const removeQuestion = (index: number) => onChange({ ...content, questions: content.questions.filter((_, i) => i !== index) });

    return (
        <>
            <p className="text-xs text-gray-500 flex items-center gap-1.5">
                <HelpCircle className="w-3.5 h-3.5 text-gray-400" />
                {/* Stop claiming every question gates progress once one of them
                    cannot be graded — a fritext question always passes. */}
                {(content.questions || []).some(isOpen)
                    ? 'Alla flervalsfrågor måste besvaras rätt för att gå vidare. Fritextsvar rättas inte.'
                    : 'Alla frågor måste besvaras rätt för att gå vidare.'}
            </p>
            <div className="space-y-3">
                {(content.questions || []).map((q, qIdx) => (
                    <div key={qIdx} className="rounded-xl border border-gray-200 bg-gray-50/60 p-3.5 space-y-2.5">
                        <div className="flex items-center justify-between">
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1A4D4F]">
                                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#1A4D4F]/10 text-[11px]">{qIdx + 1}</span>
                                Fråga {qIdx + 1}
                            </span>
                            <button type="button" onClick={() => removeQuestion(qIdx)} className="p-1 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded">
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <input
                            type="text"
                            value={q.question_text}
                            onChange={(e) => updateQuestion(qIdx, { ...q, question_text: e.target.value })}
                            placeholder="Frågetext"
                            className={INPUT}
                        />
                        <AnswerTypePicker
                            value={q.question_type}
                            onChange={(question_type) => updateQuestion(qIdx, { ...q, question_type })}
                        />
                        {!isOpen(q) && (
                        <div className="space-y-2">
                            {(q.options ?? []).map((opt, oIdx) => (
                                <OptionRow
                                    key={opt.option_id}
                                    letter={opt.option_id}
                                    text={opt.text}
                                    isCorrect={q.correct_option_id === opt.option_id}
                                    canRemove={(q.options ?? []).length > 2}
                                    onText={(v) => {
                                        const newOptions = [...(q.options ?? [])];
                                        newOptions[oIdx] = { ...opt, text: v };
                                        updateQuestion(qIdx, { ...q, options: newOptions });
                                    }}
                                    onCorrect={() => updateQuestion(qIdx, { ...q, correct_option_id: opt.option_id })}
                                    onRemove={() => updateQuestion(qIdx, { ...q, options: (q.options ?? []).filter((_, i) => i !== oIdx) })}
                                />
                            ))}
                            <button
                                type="button"
                                onClick={() => {
                                    const existing = q.options ?? [];
                                    const nextLetter = String.fromCharCode(65 + existing.length);
                                    updateQuestion(qIdx, { ...q, options: [...existing, { option_id: nextLetter, text: '' }] });
                                }}
                                className="inline-flex items-center gap-1 text-xs font-medium text-[#1A4D4F] hover:text-[#1A4D4F]/80"
                            >
                                <Plus className="w-3.5 h-3.5" /> Lägg till alternativ
                            </button>
                        </div>
                        )}
                        <ExplanationFields
                            compact
                            value={q}
                            onChange={(patch) => updateQuestion(qIdx, { ...q, ...patch })}
                            generalOnly={isOpen(q)}
                        />
                    </div>
                ))}
            </div>
            <button type="button" onClick={addQuestion} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl border border-dashed border-[#1A4D4F]/30 text-[#1A4D4F] hover:bg-[#A4C3B2]/15 w-full justify-center">
                <Plus className="w-4 h-4" /> Lägg till fråga
            </button>
        </>
    );
};

export default ModuleStepEditor;
